import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Car, Shield, ChevronDown } from 'lucide-react';

function UninsuredVehiclesModal({ uninsuredVehicles, activeContracts, onClose, onLaunchAddTool }) {
  const { t } = useTranslation();
  const [selectedContractId, setSelectedContractId] = useState('');

  const selectedContract = activeContracts.find(c => c.id === parseInt(selectedContractId));

  const handleInsure = () => {
    if (selectedContract) {
      onLaunchAddTool(selectedContract);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[750px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('insurance.uninsuredModal.title', { count: uninsuredVehicles.length })}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Warning Banner */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <div className="text-sm text-red-800">
              <p className="font-semibold mb-1">{t('insurance.uninsuredModal.warningTitle')}</p>
              <p>{t('insurance.uninsuredModal.warningBody')}</p>
            </div>
          </div>

          {/* Vehicle Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="pb-3 pr-4">{t('insurance.uninsuredModal.col.plate')}</th>
                  <th className="pb-3 pr-4">{t('insurance.uninsuredModal.col.makeModel')}</th>
                  <th className="pb-3">{t('insurance.uninsuredModal.col.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {uninsuredVehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{vehicle.license_plate || t('common.states.notAvailable')}</span>
                      </div>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">
                      {`${vehicle.make || ''} ${vehicle.model || ''}`.trim() || '—'}
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                        {t('insurance.uninsuredModal.uninsured')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {uninsuredVehicles.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 text-green-300 mx-auto mb-3" />
              <p className="font-medium text-green-600">{t('insurance.uninsuredModal.allCovered')}</p>
            </div>
          )}

          {/* Action Section */}
          {uninsuredVehicles.length > 0 && activeContracts.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
              <h3 className="text-sm font-semibold text-gray-700">{t('insurance.uninsuredModal.insureThese')}</h3>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <select
                    value={selectedContractId}
                    onChange={(e) => setSelectedContractId(e.target.value)}
                    className="w-full appearance-none px-4 py-2.5 pr-10 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{t('insurance.vehicleTab.selectContract')}</option>
                    {activeContracts.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.policy_number} — {c.insurer_name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
                <button
                  onClick={handleInsure}
                  disabled={!selectedContract}
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedContract
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {t('insurance.uninsuredModal.insureThese')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UninsuredVehiclesModal;
